const INSCRIPTION_ID_PATTERN = /^[0-9a-f]{64}i(?:0|[1-9][0-9]{0,9})$/;
const ASSET_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const HIVE_MAX_FILE_BYTES = 25_000_000;
export const HIVE_IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"] as const;
export const HIVE_IMAGE_ACCEPT = HIVE_IMAGE_MIME_TYPES.join(",");

export function validateHiveImageFile(file: Pick<File, "size" | "type">): string | null {
  const mimeType = file.type.split(";", 1)[0]!.trim().toLowerCase();
  if (!(HIVE_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType)) return "Hive supports JPEG, PNG, WEBP, GIF, and AVIF images.";
  if (!Number.isSafeInteger(file.size) || file.size < 1) return "Hive images must not be empty.";
  if (file.size > HIVE_MAX_FILE_BYTES) return "Hive images must be 25 MB or smaller.";
  return null;
}

export interface HiveUploadAuthorization {
  uploadId: string;
  uploadUrl: string;
  authorization: string;
  expiresAt: number;
  name: string;
  mimeType: string;
  size: number;
}

export interface HiveUploadReceipt {
  uploadId: string;
  assetId: string;
  mimeType: string;
  size: number;
  sha256: string;
}

export interface HiveUploadReady extends HiveUploadReceipt {
  body: Uint8Array;
}

export function buildHiveReferenceHtml(clientInscriptionId: string, assetId: string): string {
  const client = clientInscriptionId.trim().toLowerCase();
  const asset = assetId.trim();
  if (!INSCRIPTION_ID_PATTERN.test(client)) throw new Error("Hive media client is not configured");
  if (!ASSET_ID_PATTERN.test(asset)) throw new Error("Hive returned an invalid media reference");
  return `<style>html,body,iframe{border:0;margin:0;width:100%;height:100%;overflow:hidden}iframe{display:block}</style><iframe src=/content/${client}?id=${asset}></iframe>`;
}

export function buildHiveReferenceBody(clientInscriptionId: string, assetId: string): Uint8Array {
  return new TextEncoder().encode(buildHiveReferenceHtml(clientInscriptionId, assetId));
}

export function uploadHiveFileDirect(
  file: File,
  permit: HiveUploadAuthorization,
  onProgress: (percent: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", permit.uploadUrl);
    request.responseType = "json";
    request.setRequestHeader("authorization", `Bearer ${permit.authorization}`);
    request.setRequestHeader("content-type", permit.mimeType);
    request.setRequestHeader("accept", "application/json");
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
    });
    request.addEventListener("load", () => {
      const body = request.response as { receipt?: unknown; error?: unknown } | null;
      if (request.status >= 200 && request.status < 300 && typeof body?.receipt === "string") {
        onProgress(100);
        resolve(body.receipt);
        return;
      }
      reject(new Error(typeof body?.error === "string" ? body.error : `Hive upload failed (${request.status || "network"})`));
    });
    request.addEventListener("error", () => reject(new Error("Hive upload could not reach the media service")));
    request.addEventListener("abort", () => reject(new Error("Hive upload was cancelled")));
    request.send(file);
  });
}
